'use client';
import { motion } from 'motion/react';

const secondaryQuotes = [
  {
    text: 'The Ask screen alone replaced three phone calls I used to make every morning. I type a question, I get a number. No opinions.',
    name: 'Priya Mehta',
    role: 'VP Sales, Regional FMCG Brand',
    initials: 'PM',
  },
  {
    text: "We identified ₹22L in dormant retailer revenue in the first week. Sent reactivation schemes via WhatsApp that afternoon. That's the speed.",
    name: 'Arun Krishnan',
    role: 'Trade Marketing Head',
    initials: 'AK',
  },
  {
    text: 'My RSMs used to say "everything is fine." Now I can see the data before the call. The conversations are completely different.',
    name: 'Sanjay Gupta',
    role: 'National Sales Director',
    initials: 'SG',
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

export default function Testimonials() {
  return (
    <section
      className="relative overflow-hidden border-t border-border py-24 sm:py-32"
      aria-labelledby="testimonial-heading"
    >
      {/* Subtle warm wash */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            'radial-gradient(ellipse at 20% 0%, hsl(30 95% 52% / 0.04), transparent 50%)',
        }}
      />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <p
          className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground"
          id="testimonial-heading"
        >
          What Leaders Say
        </p>

        {/* Primary testimonial */}
        <motion.figure
          className="mt-10 max-w-4xl"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
          variants={fadeUp}
        >
          {/* Giant amber quote mark */}
          <span
            className="block font-serif text-[120px] leading-none text-accent/20 sm:text-[160px]"
            aria-hidden="true"
          >
            &ldquo;
          </span>

          <blockquote className="-mt-16 sm:-mt-20">
            <p className="font-serif text-2xl font-400 leading-relaxed text-foreground sm:text-3xl md:text-[2.5rem] md:leading-[1.3]">
              I used to spend my first 90 minutes every morning on Excel
              and WhatsApp, trying to piece together what happened yesterday.
              Now I know exactly where the problems are, why they&apos;re
              happening, and what to do — in 10 minutes.{' '}
              <span className="text-accent">I can&apos;t go back.</span>
            </p>
          </blockquote>

          <figcaption className="mt-8 flex items-center gap-4">
            <img
              src="https://i.pravatar.cc/96?img=68"
              alt="Rajesh Subramaniam"
              width={44}
              height={44}
              loading="lazy"
              className="h-11 w-11 rounded-full object-cover"
            />
            <div>
              <p className="text-sm font-semibold text-foreground">
                Rajesh Subramaniam
              </p>
              <p className="text-sm text-muted-foreground">
                National Sales Manager, Leading FMCG Brand
              </p>
            </div>
          </figcaption>
        </motion.figure>

        {/* Secondary quotes grid */}
        <div className="mt-16 grid gap-6 sm:mt-20 md:grid-cols-3">
          {secondaryQuotes.map((q, i) => (
            <motion.figure
              key={q.name}
              className="rounded-2xl border border-border bg-white p-6 sm:p-7"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              variants={fadeUp}
            >
              <blockquote>
                <p className="text-sm leading-relaxed text-foreground">
                  &ldquo;{q.text}&rdquo;
                </p>
              </blockquote>
              <figcaption className="mt-5 flex items-center gap-3">
                <div
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-[10px] font-bold text-foreground"
                  aria-hidden="true"
                >
                  {q.initials}
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground">
                    {q.name}
                  </p>
                  <p className="text-xs text-muted-foreground">{q.role}</p>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
